import React, { createContext, useState, useContext, useRef, useCallback, useEffect } from 'react';
import alaram from "../../context/alaram.mp3";

const NotificationContext = createContext();

export const NotificationProvider = ({ children }) => {
  // Track notifications for each tab
  const [notifications, setNotifications] = useState({
    'orders': 0,          // Food user orders
    'Live orders': 0,     // Food live orders
    'Delivery orders': 0, // Delivery live orders
    'Online': 0           // Online restaurants
  });

  const audioRef = useRef(null);
  const isAudioPlayingRef = useRef(false);
  const audioTimeoutRef = useRef(null);

  // Initialize audio
  useEffect(() => {
    audioRef.current = new Audio(alaram);
    audioRef.current.load();

    // Request notification permission
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission();
    }

    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      if (audioTimeoutRef.current) {
        clearTimeout(audioTimeoutRef.current);
      }
    };
  }, []);

  // Play notification sound (global)
  const playNotificationSound = useCallback(() => {
    if (!audioRef.current || isAudioPlayingRef.current) return;

    try {
      isAudioPlayingRef.current = true;
      audioRef.current.currentTime = 0;
      
      const playPromise = audioRef.current.play();
      
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            // Audio is playing
            audioRef.current.onended = () => {
              isAudioPlayingRef.current = false;
            };
          })
          .catch((error) => {
            console.warn('Audio playback failed:', error);
            isAudioPlayingRef.current = false;
          });
      }

      // Reset audio playing flag after 3 seconds (in case of issues)
      if (audioTimeoutRef.current) {
        clearTimeout(audioTimeoutRef.current);
      }
      audioTimeoutRef.current = setTimeout(() => {
        isAudioPlayingRef.current = false;
      }, 3000);

    } catch (error) {
      console.warn('Audio playback error:', error);
      isAudioPlayingRef.current = false;
    }
  }, []);

  // Show browser notification
  const showBrowserNotification = useCallback((tabName, count) => {
    if (!("Notification" in window)) return;
    
    if (Notification.permission === "granted") {
      const tabDisplayNames = {
        'orders': 'Food Orders',
        'Live orders': 'Live Food Orders',
        'Delivery orders': 'Delivery Orders',
        'Online': 'Online Restaurants'
      };
      
      const displayName = tabDisplayNames[tabName] || tabName;
      
      new Notification(`🆕 ${count} New ${displayName}!`, {
        body: `Click to view ${count} new ${displayName.toLowerCase()}.`,
        icon: tabName === 'Delivery orders' ? "🛵" : "🍽️",
        silent: true
      });
    }
  }, []);

  // Add notification for a specific tab
  const addNotification = useCallback((tabName, count = 1) => {
    setNotifications(prev => ({
      ...prev,
      [tabName]: (prev[tabName] || 0) + count
    }));

    // Play sound for any new notification
    playNotificationSound();
    
    // Show browser notification
    showBrowserNotification(tabName, count);

    // Clear audio flag after a short time to allow future notifications
    setTimeout(() => {
      isAudioPlayingRef.current = false;
    }, 2000);
  }, [playNotificationSound, showBrowserNotification]);

  // Clear notifications for a specific tab (when user visits)
  const clearNotifications = useCallback((tabName) => {
    setNotifications(prev => ({
      ...prev,
      [tabName]: 0
    }));
  }, []);

  // Clear all notifications
  const clearAllNotifications = useCallback(() => {
    setNotifications({
      'orders': 0,
      'Live orders': 0,
      'Delivery orders': 0,
      'Online': 0
    });
  }, []);

  // Get total notification count
  const getTotalNotifications = useCallback(() => {
    return Object.values(notifications).reduce((sum, count) => sum + count, 0);
  }, [notifications]);

  return (
    <NotificationContext.Provider value={{
      notifications,
      addNotification,
      clearNotifications,
      clearAllNotifications,
      getTotalNotifications,
      playNotificationSound
    }}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};